'use client';

import { useState, useEffect } from 'react';
import { CountUpDisplay } from '../atomic/CountUpDisplay';
import { InfoTooltip } from '../atomic/Tooltip';
import { ConfettiCelebration } from '../atomic/ConfettiCelebration';

interface PotentialDisplayProps {
  value: number;
  title?: string;
  description?: string;
  einwohner?: number;
  haushalte?: number;
  className?: string;
  gemeindeName?: string;
}

export function PotentialDisplay({
  value,
  title,
  description = "JÄHRLICHES POTENZIAL",
  einwohner,
  haushalte,
  className = '',
  gemeindeName
}: PotentialDisplayProps) {
  const [showPulse, setShowPulse] = useState(false);
  const [showConfetti, setShowConfetti] = useState(false);

  const handleCountComplete = () => {
    setShowPulse(true);
    // Show confetti for high-performing gemeinden (> €100,000)
    if (value > 100000) {
      setShowConfetti(true);
    }
  };

  useEffect(() => {
    return () => {
      setShowPulse(false);
      setShowConfetti(false);
    };
  }, []);

  return (
    <>
      <ConfettiCelebration trigger={showConfetti} />
      <div className={`bg-white rounded-xl shadow-md p-6 relative ${className} ${showPulse ? 'animate-pulse-once' : ''}`}>
        {/* Header with title */}
        <div className="mb-2">
          {title && <h2 className="text-2xl md:text-3xl font-bold text-gray-800">{title}</h2>}
        </div>
      {description && (
        <div className="text-sm font-medium text-gray-500 mb-4">
          {description}
          <InfoTooltip 
            content={{
              title: "Berechnungsgrundlage",
              description: "40% der handelbaren Energie × zusätzliche EEG-Einsparungen (IKB: 0,093 €/kWh, TIWAG: 0,087 €/kWh). Basierend auf durchschnittlichen Marktdaten und aktuellen Netzentgelten 2025."
            }}
          />
        </div>
      )}
      <div className="flex items-center">
        <span className="text-4xl md:text-5xl font-bold text-green-600">
          <CountUpDisplay 
            value={value} 
            prefix="€" 
            onComplete={handleCountComplete}
            className="inline-flex"
          />
        </span>
      </div>
      
      {/* Per-capita and per-household metrics */}
      {(einwohner || haushalte) && (
        <div className="mt-4 pt-4 border-t border-gray-200 dark:border-gray-700">
          <div className="grid grid-cols-2 gap-4">
            {einwohner && (
              <div className="text-center">
                <div className="text-2xl font-bold text-green-600">
                  €{Math.round(value / einwohner).toLocaleString()}
                </div>
                <div className="text-xs text-gray-500">
                  pro Einwohner
                  <InfoTooltip 
                    content={{
                      title: "Pro-Kopf-Berechnung",
                      description: `Bei ${einwohner.toLocaleString()} Einwohnern entspricht das €${Math.round(value / einwohner)} pro Person und Jahr.`
                    }}
                  />
                </div>
              </div>
            )}
            {haushalte && (
              <div className="text-center">
                <div className="text-2xl font-bold text-green-600">
                  €{Math.round(value / haushalte).toLocaleString()}
                </div>
                <div className="text-xs text-gray-500">
                  pro Haushalt
                  <InfoTooltip 
                    content={{
                      title: "Pro-Haushalt-Berechnung",
                      description: `Bei ${haushalte.toLocaleString()} Haushalten entspricht das €${Math.round(value / haushalte)} pro Haushalt und Jahr.`
                    }}
                  />
                </div>
              </div>
            )}
          </div>
        </div>
      )}
      
      <style jsx>{`
        @keyframes pulse-once {
          0% { box-shadow: 0 0 0 0 rgba(0, 132, 61, 0.7); }
          70% { box-shadow: 0 0 0 15px rgba(0, 132, 61, 0); }
          100% { box-shadow: 0 0 0 0 rgba(0, 132, 61, 0); }
        }
        
        .animate-pulse-once {
          animation: pulse-once 2s ease-out 1;
        }
      `}</style>
    </div>
    </>
  );
}
